import { useEffect, useMemo, useState } from 'react';
import { classifyLinkViewError, ShareLinkViewErrorScreen, type ShareLinkViewErrorKind } from '@indipia/shared-features';
import { worklogApi } from '../api/worklogApi';
import { CustomerReportView, DEFAULT_SETTINGS, type ReportGroup, type ReportSettings } from '../components/CustomerReportView';
import type { ProcessStep, StepStatus } from '../components/process-chart';
import type { BasicInfoWorklog, CustomerReportPublic } from '../types/worklog.types';

/**
 * 고객 공개 리포트 (외부·무인증 공유 화면) — API명세 §4.1.13. 스냅샷 기준(결정 5-11).
 *
 * 고객이 알림톡(`WORKLOG00CUST00REPORT01`) 버튼으로 여는 공개 뷰. RequireAuth·AppFrame 바깥
 * (F-04 §7)에서 `/share/CUSTOMER_REPORT?shareId=...` 로 진입한다. 자격증명은 pf-sharegate 발급
 * 공유 쿠키(shareScope)이며, shareId는 서버가 X-Share-Scope 에서 복원한다(URL 미노출).
 *
 * 본문은 `CustomerReportView`(공유·미리보기 공통 렌더, 설계서 §5.6 "유지")를 그대로 쓴다 — 고객공유
 * 설정 화면(CustomerReportSettingPage)·미리보기 화면(CustomerReportPreviewPage)은 폐기됐지만(§5.6),
 * 이 컴포넌트 자체는 이 공개 뷰의 유일한 소비처로 남는다. 설정 변경 UI가 없어진 이후로는
 * displayProgressYn·displayChartYn·photoColCnt 가 생성 시점 기본값으로 고정된다(CustomerReport.createDefault).
 */

const DOW = ['일', '월', '화', '수', '목', '금', '토'];
const pad2 = (n: number) => String(n).padStart(2, '0');

const parseDate = (d: string | null): Date | null => {
  if (!d) return null;
  const x = new Date(`${d}T00:00:00`);
  return Number.isNaN(x.getTime()) ? null : x;
};
const md = (d: string | null): string => {
  const x = parseDate(d);
  return x ? `${x.getMonth() + 1}.${x.getDate()}` : '';
};
/**
 * 공정 시작~종료 기간 표시.
 * - 시작=종료(1일짜리 공정)면 시작일자만: 7.13
 * - 종료가 시작과 같은 연·월이면 종료는 일자만: 7.13~15
 * - 월을 넘어가면 종료도 월.일: 7.31~8.1
 * 한쪽만 있으면 그 날짜만 표시한다.
 */
const mdRange = (start: string | null, end: string | null): string => {
  const s = parseDate(start);
  const e = parseDate(end);
  if (s && e) {
    const sameYear = s.getFullYear() === e.getFullYear();
    if (sameYear && s.getMonth() === e.getMonth()) {
      return s.getDate() === e.getDate() ? md(start) : `${md(start)}~${e.getDate()}`;
    }
    return `${md(start)}~${md(end)}`;
  }
  return md(end) || md(start);
};
const calDays = (start: string | null, end: string | null): string | undefined => {
  const s = parseDate(start);
  const e = parseDate(end);
  if (!s || !e) return undefined;
  const n = Math.round((e.getTime() - s.getTime()) / 86400000) + 1;
  return n > 0 ? `${n}일` : undefined;
};
/** 오늘 기준 공정 상태: 종료 지남→완료, 시작~종료 사이→시공중, 시작 전→시작전. */
function statusOf(start: string | null, end: string | null, today: Date): StepStatus {
  const s = parseDate(start);
  const e = parseDate(end);
  if (e && today.getTime() > e.getTime()) return 'done';
  if (s && e && today.getTime() >= s.getTime() && today.getTime() <= e.getTime()) return 'active';
  if (s && today.getTime() < s.getTime()) return 'pending';
  if (!s && e && today.getTime() <= e.getTime()) return 'pending';
  return 'pending';
}

/**
 * 공정 목록(§4.1.13 workTypes) → 공정 스텝. 스케줄 실재 시 마지막에 '완공' 스텝을 추가한다.
 * hasSchedule=false 면 '완공' 스텝을 붙이지 않는다(스케줄 없는 현장은 완공 표기 미노출).
 */
function buildStepsFrom(
  workTypes: { workTypeName: string | null; startDate: string | null; endDate: string | null }[],
  hasSchedule: boolean,
  finishDate: string | null,
): { steps: ProcessStep[]; hasSchedule: boolean } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const steps: ProcessStep[] = workTypes
    .slice()
    .sort((a, b) => (a.startDate ?? '9999').localeCompare(b.startDate ?? '9999'))
    .map((wt) => ({
      name: wt.workTypeName ?? '',
      status: statusOf(wt.startDate, wt.endDate, today),
      date: mdRange(wt.startDate, wt.endDate),
      days: calDays(wt.startDate, wt.endDate),
    }));

  if (hasSchedule) {
    steps.push({ name: '완공', status: 'finish', date: md(finishDate) });
  }
  return { steps, hasSchedule };
}

/** §4.1.13 reportCards(발송 스냅샷, 카드=현장보고 1건) → CustomerReportView 의 ReportGroup[]. 같은 작업일 카드를 발송순으로 머지. */
function toViewGroups(data: CustomerReportPublic): ReportGroup[] {
  const byDate = new Map<string, { contents: string[]; photos: { url: string; thumbUrl: string }[] }>();
  // 발송일시 오름차순으로 먼저 정렬 → 같은 작업일 안에서 발송 순서대로 이어붙인다.
  const sorted = [...data.reportCards].sort((a, b) => a.sentDtm.localeCompare(b.sentDtm));
  for (const card of sorted) {
    const key = card.workDate;
    const entry = byDate.get(key) ?? { contents: [], photos: [] };
    if (card.customerMemo) entry.contents.push(card.customerMemo);
    entry.photos.push(
      ...[...card.photos]
        .sort((a, b) => a.photoSeq - b.photoSeq)
        // 공개 응답은 단일 URL만 준다(썸네일 구분 없음) — 목록·라이트박스 모두 같은 원본을 쓴다.
        .map((p) => ({ url: p.url, thumbUrl: p.url })),
    );
    byDate.set(key, entry);
  }
  return [...byDate.entries()]
    .map(([key, entry]) => {
      const d = new Date(key);
      const label = Number.isNaN(d.getTime())
        ? key
        : `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} (${DOW[d.getDay()]})`;
      return { date: label, dateKey: key, contents: entry.contents, photos: entry.photos };
    })
    .filter((g) => g.contents.length > 0 || g.photos.length > 0)
    .sort((a, b) => b.dateKey.localeCompare(a.dateKey));
}

/**
 * 고객 리포트 본문(공유 화면·발송자 미리보기 공통) — 공개 응답(§4.1.13)을 CustomerReportView 로 그린다.
 * 사장님 발송자 미리보기(CustomerReportSendPreviewPage)가 같은 본문을 쓰도록 분리했다.
 */
export function CustomerReportPublicBody({ data }: { data: CustomerReportPublic }) {
  /**
   * §4.3 응답의 공개설정 — displayChartYn·photoColCnt 는 서버가 그대로 내려준다.
   * displayProgressYn 은 응답에 없어(진척율은 값 자체를 필터해서 준다) progressRate 유무로 판정한다.
   */
  const settings: ReportSettings = useMemo(() => {
    const displayChartYn = data?.displayChartYn === 'Y';
    const displayProgressYn = data?.progressRate != null;
    return {
      ...DEFAULT_SETTINGS,
      showScheduleYn: displayChartYn || displayProgressYn,
      displayProgressYn,
      displayChartYn,
      photoColCnt: data?.photoColCnt || DEFAULT_SETTINGS.photoColCnt,
    };
  }, [data]);

  // 공정 스텝 — 사장님 미리보기와 같은 변환기(buildStepsFrom)를 쓴다. workTypes 가 비면 차트 미노출.
  const process = useMemo(
    () => buildStepsFrom(data?.workTypes ?? [], (data?.workTypes?.length ?? 0) > 0, data?.constEndDate ?? null),
    [data],
  );

  // CustomerReportView 는 현장 요약을 BasicInfoWorklog 로 받는다. 공개 응답에 없는 필드(연락처·내부식별자)는
  // PII 최소화로 애초에 내려오지 않으므로 null 로 채운다 — 화면에서도 쓰이지 않는다.
  const worklog: BasicInfoWorklog | null = useMemo(() => {
    if (!data) return null;
    return {
      worklogId: data.worklogId,
      siteId: 0,
      scheduleId: null,
      siteName: data.siteName,
      siteAddress: data.siteAddress,
      customerId: null,
      customerName: data.customerName,
      customerMobile: null,
      siteLat: null,
      siteLng: null,
      progressStatus: '',
      constStartDate: data.constStartDate,
      constEndDate: data.constEndDate,
    };
  }, [data]);

  if (!worklog) return null;

  return (
    <div className="mx-auto max-w-3xl px-4 py-5">
      <CustomerReportView
        worklog={worklog}
        companyName={data.companyName}
        settings={settings}
        groups={toViewGroups(data)}
        constDays={data.constDays}
        progressRate={data.progressRate}
        steps={process.steps}
        hasSchedule={process.hasSchedule}
      />
    </div>
  );
}

export function CustomerReportSharePage() {
  const [state, setState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [data, setData] = useState<CustomerReportPublic | null>(null);
  // 만료·회수·무효·일시 오류 4종(+접속 시간 초과) — 스케줄·사진첩 열람 화면과 같은 분류 기준
  const [errorKind, setErrorKind] = useState<ShareLinkViewErrorKind>('TEMPORARY');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    worklogApi
      .getCustomerReportShare(controller.signal)
      .then((res) => {
        setData(res.data.data);
        setState('ready');
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        setErrorKind(classifyLinkViewError(err));
        setState('error');
      });
    return () => controller.abort();
  }, [reloadKey]);

  if (state === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <p className="text-sm text-text-secondary">리포트를 불러오는 중…</p>
      </div>
    );
  }

  if (state === 'error' || !data) {
    return <ShareLinkViewErrorScreen kind={errorKind} onRetry={() => setReloadKey((k) => k + 1)} />;
  }

  return (
    <div className="min-h-screen bg-surface">
      <CustomerReportPublicBody data={data} />
    </div>
  );
}
